import { useState } from 'react'
import { files } from '@/api/endpoints'
import { saveBlob } from '@/api/client'
import type { Lease } from '@/api/types'
import { CopyBox } from '@/components/CopyBox'
import { Err } from '@/components/Err'
import { isUnreachable } from '@/domain/lease'
import { ACTION } from './chips'

// 접속 정보는 게이트웨이를 지나는 두 형태뿐이다 — 명령 한 줄과 `~/.ssh/config` 조각.
// 그 아래 사설 주소는 접속 수단이 아니라 VM 끼리 닿는 주소다(같은 복사 칸에 담는다) — 같은 클러스터의 VM 은 이 주소로 서로 닿으므로
// 주인은 자기 VM 의 주소를 알아야 한다. 백엔드가 주인과 키를 받을 수 있는 관리자에게만 보내고, 공유받은 팀원에게는 비어 온다.
// VM 이 없거나 아직 준비되지 않았으면 라벨 하나만 둔다. 사용자 상세와 관리자 VM 상세가 같은 칸을 쓴다.
export function AccessPanel({ lease: l }: { lease: Lease }) {
  const a = l.access
  // 노드가 응답하지 않는 동안(B-23)은 접속 정보가 없다 — 그 한 문장만 둔다.
  if (l.state === 'active' && isUnreachable(l.vm)) {
    return <div className="rounded-lg border bg-card px-4 py-3 text-[12px] text-muted-foreground">지금은 VM 에 접속할 수 없습니다.</div>
  }
  if (l.state !== 'active' || !a) {
    return (
      <div className="rounded-lg border bg-card px-4 py-3 text-[12px] text-muted-foreground">
        {l.state === 'active' ? '접속 준비 중' : 'VM 없음'}
      </div>
    )
  }
  if (!a.ssh_command || !a.ssh_config) {
    return <div className="rounded-lg border bg-card px-4 py-3 text-[12px] text-muted-foreground">접속 준비 중</div>
  }
  return (
    <div className="grid gap-3 rounded-lg border bg-card px-4 py-3">
      <div className="grid gap-1.5">
        <span className="text-[11px] font-semibold tracking-wide text-muted-foreground uppercase">접속 명령어</span>
        <CopyBox text={a.ssh_command} label="접속 명령어 복사" />
        {/* 생성 키의 파일 이름은 `<소유자>-<VM 이름>.key` 이고 백엔드가 정한다. 명령이 그 파일을 쓸 때만 저장하라고 한다 —
            자기 기기 키를 등록한 소유자의 명령에는 파일이 없다(#628). */}
        {l.ssh_key_file && a.ssh_command.includes(l.ssh_key_file) ? (
          <div className="text-[11px] text-muted-foreground">
            <span className="font-mono">{l.ssh_key_file}</span> 를 <span className="font-mono">~/.ssh</span> 에 저장하세요.
          </div>
        ) : null}
      </div>
      <div className="grid gap-1.5">
        <span className="text-[11px] font-semibold tracking-wide text-muted-foreground uppercase">~/.ssh/config</span>
        <CopyBox text={a.ssh_config} label="ssh config 복사" />
      </div>
      {a.ipv4 ? (
        <div className="grid gap-1.5">
          <span className="text-[11px] font-semibold tracking-wide text-muted-foreground uppercase">사설 주소</span>
          <CopyBox text={a.ipv4} label="사설 주소 복사" />
        </div>
      ) : null}
    </div>
  )
}

// 생성 키는 첫 접속 전까지 다시 받을 수 있다(B-10). 링크가 있는 동안만 칩이 있고, 받은 파일은 화면에 남지 않는다.
export function KeyDownload({ lease: l }: { lease: Lease }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const url = l.ssh_private_key_download_url
  if (!url) return null
  const get = async () => {
    setBusy(true)
    setError(null)
    try {
      const d = await files.downloadOnce(url)
      saveBlob(d, l.ssh_key_file ?? `${l.owner.username}-${l.name}.key`)
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }
  return (
    <>
      <button type="button" className={ACTION} onClick={get} disabled={busy}>
        키 받기
      </button>
      <Err error={error} />
    </>
  )
}
